import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Chip } from '@ardoq/status-ui';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { isPresentationMode } from 'env';
import { t, type I18nLocale } from '@ardoq/i18n';
import { VISUALIZATION_EXPORT_HEADER_CLASS_NAME } from '@ardoq/global-consts';

export enum Option {
  CENTER,
  LEFT,
  RIGHT,
}

const CENTER_WIDTH = 90;

const CloseChip = styled.div`
  border-radius: 12px;
  margin-left: 8px;
  width: 24px;
  height: 24px;
  border: 1px solid ${colors.grey300};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.grey700};
  cursor: pointer;
  &:hover {
    border: 1px solid ${colors.grey700};
  }

  .forExport & {
    display: none;
  }
`;

const OuterContainer = styled.div.attrs({
  className: VISUALIZATION_EXPORT_HEADER_CLASS_NAME,
})<{ $disabled?: boolean }>`
  position: absolute;
  right: 20px;
  top: 50px;
  background-color: white;
  border-radius: 18px;
  display: flex;
  align-items: center;
  * {
    transition: none !important;
  }

  ${props =>
    props.$disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

const Container = styled.div`
  display: flex;
`;

// Temporary fix.
// Current Chip from @ardoq/legacy has box-sizing: content-box.
const FixedChip = styled(Chip)`
  height: 34px;
`;

const LeftOption = styled(FixedChip)`
  border-radius: 16px;
  margin: 0;
  padding-right: ${CENTER_WIDTH + 4}px;
  &:hover {
    z-index: 1;
  }
`;

const Ellipsis = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightOption = styled(FixedChip)`
  border-radius: 16px;
  margin: 0;
  padding-left: ${CENTER_WIDTH + 4}px;
  max-width: 250px;
  &:hover {
    z-index: 1;
  }
`;

const CenterOption = styled(FixedChip)`
  position: relative;
  background: ${colors.grey200};
  border: 1px solid ${colors.grey300};
  width: ${CENTER_WIDTH}px;
  margin: 0 -${CENTER_WIDTH}px;
  z-index: 3;

  ${props =>
    props.isActive &&
    css`
      background: ${colors.grey700};
      border: 1px solid ${colors.grey900};
      color: white;
    `}
`;

const BorderPainter = styled.div<{
  $hasBorderLeft?: boolean;
  $hasBorderRight?: boolean;
}>`
  position: absolute;
  left: -1px;
  top: -1px;
  width: ${CENTER_WIDTH}px;
  height: 34px;
  border: 1px solid transparent;

  ${props =>
    props.$hasBorderLeft &&
    css`
      border-radius: 16px 0 0 16px;
      border-color: ${colors.grey700};
      border-right-color: transparent;
    `}

  ${props =>
    props.$hasBorderRight &&
    css`
      border-radius: 0 16px 16px 0;
      border-color: ${colors.grey700};
      border-left-color: transparent;
    `}
`;

const VisualDiffSwitcher = ({
  scenarioName,
  activeOption,
  optionCallbacks,
  disabled,
  className,
  onCloseDiffMode,
  i18nLocale,
}: {
  scenarioName?: string;
  disabled?: boolean;
  activeOption: Option | null;
  optionCallbacks: Record<Option, () => void>;
  className?: string;
  onCloseDiffMode?: () => void;
  i18nLocale: I18nLocale;
}) => {
  const closeLabel = t(
    i18nLocale,
    'tabview:blockDiagram.visualDiff.close',
    'Close visual diff'
  );
  const [isOver, setIsOver] = useState<null | Option>(null);
  const isRightActive =
    activeOption === Option.RIGHT || activeOption === Option.CENTER;
  const isCenterActive = activeOption === Option.CENTER;
  const isLeftActive =
    activeOption === Option.LEFT || activeOption === Option.CENTER;

  return (
    <OuterContainer className={className} $disabled={disabled}>
      <Container>
        <LeftOption
          onMouseEnter={() => setIsOver(Option.LEFT)}
          onMouseLeave={() => setIsOver(null)}
          isActive={isLeftActive}
          onClick={optionCallbacks[Option.LEFT]}
          icon={
            <Icon
              iconName={IconName.MASTER}
              style={
                isLeftActive
                  ? { color: colors.grey400 }
                  : { color: colors.grey300 }
              }
            />
          }
        >
          {t(
            i18nLocale,
            'tabview:blockDiagram.visualDiff.mainline',
            'Mainline'
          )}
        </LeftOption>

        <CenterOption
          isActive={isCenterActive}
          onClick={optionCallbacks[Option.CENTER]}
          icon={
            <Icon
              iconName={IconName.DIFF}
              style={
                isCenterActive ? { color: 'white' } : { color: colors.grey300 }
              }
            />
          }
        >
          <BorderPainter
            $hasBorderRight={
              !isCenterActive && isLeftActive && isOver === Option.LEFT
            }
            $hasBorderLeft={
              !isCenterActive && isRightActive && isOver === Option.RIGHT
            }
          />
          {t(i18nLocale, 'tabview:blockDiagram.visualDiff.diff', 'Diff')}
        </CenterOption>
        <RightOption
          onMouseEnter={() => setIsOver(Option.RIGHT)}
          onMouseLeave={() => setIsOver(null)}
          isActive={isRightActive}
          onClick={optionCallbacks[Option.RIGHT]}
          icon={
            <Icon
              iconName={IconName.SCENARIO}
              style={{
                flexShrink: 0,
                ...(isRightActive
                  ? { color: colors.green600 }
                  : { color: colors.grey300 }),
              }}
            />
          }
        >
          <Ellipsis>{scenarioName || 'Current scenario'}</Ellipsis>
        </RightOption>
      </Container>
      {!isPresentationMode() && (
        <CloseChip
          onClick={onCloseDiffMode}
          data-tooltip-text={closeLabel}
          aria-label={closeLabel}
        >
          <Icon iconName={IconName.CLOSE} iconSize={IconSize.SMALL} />
        </CloseChip>
      )}
    </OuterContainer>
  );
};

export { VisualDiffSwitcher };
